import { getVenuaPlannerRegularTransportSuggestions } from '@/api/generated/venua-planner-planning/venua-planner-planning';
import type { ListRequest, RegularTransportSuggestion } from '@/api/generated/schemas';
import { RegularTransportMode } from '@/api/generated/schemas';
import {
  defineEntityAsyncSelector,
  listRequestQueryKey,
  toListRequest,
  type NoPickFilters,
} from '@/shared/ui/async-selector';
import { Badge, type BadgeTone } from '@/shared/ui/badge';
import { cn } from '@/shared/lib/utils';

/** Sdílená doprava tak, jak ji drží záložka Doprava. */
export interface RegularTransportRef {
  regularTransportId: number;
  name: string;
  mode: RegularTransportMode;
  termCount: number;
  /** Krajní stanice jednotlivých legů v pořadí — z nich se skládá směrování. */
  routing: string[];
}

const MODE_TONE: Record<RegularTransportMode, BadgeTone> = {
  [RegularTransportMode.RoundTrip]: 'info',
  [RegularTransportMode.OneWay]: 'warning',
  [RegularTransportMode.Undetermined]: 'draft',
};

/**
 * Směrování dopravy = řetězec krajních stanic všech legů, např. `WAW→LAX→WAW`. Bere se z předpisu
 * linky; duplicitní přestupní stanice mezi navazujícími legy se neopakuje.
 */
function toRouting(item: RegularTransportSuggestion): string[] {
  const stops: string[] = [];
  item.legs.forEach((leg) => {
    const from = leg.fromCode ?? '—';
    const to = leg.toCode ?? '—';
    if (stops.length === 0) {
      stops.push(from);
    } else if (stops[stops.length - 1] !== from) {
      stops.push(from);
    }
    stops.push(to);
  });
  return stops;
}

/** Směrování s ztlumenými šipkami — tučné mono, jak chce design. */
function Routing({ stops, className }: { stops: string[]; className?: string }) {
  return (
    <span className={cn('font-mono font-semibold whitespace-nowrap', className)}>
      {stops.map((stop, index) => (
        <span key={`${stop}-${index}`}>
          {index > 0 ? <span className="text-phx-text-faint mx-1">→</span> : null}
          {stop}
        </span>
      ))}
    </span>
  );
}

/** Badge režimu nákupu letenek (RT / OW / bez určení) s vysvětlujícím tooltipem. */
export function TransportModeBadge({
  mode,
  t,
}: {
  mode: RegularTransportMode;
  t: (key: string) => string;
}) {
  return (
    <Badge
      appearance="soft"
      tone={MODE_TONE[mode]}
      title={t(`regularTransportSelector.mode.${mode}Hint`)}
    >
      {t(`regularTransportSelector.mode.${mode}`)}
    </Badge>
  );
}

/**
 * Selektor sdílené dopravy.
 *
 * Vybírá se **celá doprava**, ne jednotlivý leg — proto se ukazuje celé směrování; který leg se do
 * kterého směru použije, rozhodne až server po přidání.
 *
 * Filtry nabídky (letecká doprava, návaznost na konec řetězu, přelet hranic, vyloučení už použitých
 * doprav) chodí přes `extraFilters` — jsou to doménová omezení call-site, ne uživatelská filtrační lišta.
 */
const regularTransportSelector = defineEntityAsyncSelector<
  RegularTransportRef,
  RegularTransportSuggestion,
  ListRequest,
  NoPickFilters
>({
  api: {
    fetch: getVenuaPlannerRegularTransportSuggestions,
    buildApiParams: (ctx) => toListRequest(ctx.search, ctx.ids, ctx.extraFilters),
    queryKey: (request) =>
      listRequestQueryKey('venua-planner.regular-transports.suggestions', request),
    mapItem: (item) => ({
      regularTransportId: item.regularTransportId,
      name: item.name,
      mode: item.mode,
      termCount: item.termCount,
      routing: toRouting(item),
    }),
  },
  key: (item) => item.regularTransportId,
  // Sdílená doprava se zakládá i edituje v legacy agendě — proklik na řádku otevře její detail
  // a tři tečky (⋯) její seznam v Magic desktopu.
  objectCategory: 'RegularTransport',
  listPicker: 'legacy',
  display: {
    kind: 'nameCode',
    name: (item) => item.name,
    code: (item) => item.routing.join(' → '),
  },
  columns: (t) => [
    {
      id: 'routing',
      header: t('regularTransportSelector.columns.routing'),
      flex: '0 0 152px',
      cell: (item) => <Routing stops={item.routing} />,
    },
    {
      id: 'name',
      header: t('regularTransportSelector.columns.name'),
      sortable: true,
      cell: (item) => (
        <span className="truncate" title={item.name}>
          {item.name}
        </span>
      ),
    },
    {
      id: 'mode',
      // Badge je vzdy dvou- az triznakovy, sirsi nez jeho obsah byt nemusi.
      header: t('regularTransportSelector.columns.mode'),
      flex: '0 0 56px',
      cell: (item) => <TransportModeBadge mode={item.mode} t={t} />,
    },
    {
      id: 'terms',
      header: t('regularTransportSelector.columns.terms'),
      flex: '0 0 84px',
      align: 'right',
      numeric: true,
      muted: true,
      cell: (item) => t('regularTransportSelector.termCount', { count: item.termCount }),
    },
  ],
  i18n: { namespace: 'venua-planner' },
});

export const RegularTransportSelector = regularTransportSelector.Selector;
